import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Chip,
    Stack,
    Box,
    Typography,
    Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import CommonTable from '../UI/Table/CommonTable';

/**
 * ClientTable component for displaying and managing client data
 */
const ClientTable = ({
    tableData = [],
    handleEdit,
    handleDelete,
    isLoading = false,
    actionButtons = null
}) => {
    // State management
    const [selectedRow, setSelectedRow] = useState(null);

    // Memoized column definitions
    const columns = useMemo(() => [
        { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
        { key: 'client_code', label: 'Client', minWidth: 150 },
        { key: 'client_logo', label: 'Logo', minWidth: 100 },
        { key: 'status', label: 'Status', minWidth: 100 },
        { key: 'page_privileges', label: 'Page Privileges', minWidth: 250 },
        { key: 'created_at', label: 'Created', minWidth: 100 },
        { key: 'actions', label: 'Actions', minWidth: 100 }
    ], []);

    const handleRowSelect = (row) => {
        setSelectedRow(selectedRow?.client_id === row.client_id ? null : row);
    };

    const confirmDelete = (clientId, clientCode) => {
        if (window.confirm(`Are you sure you want to delete client "${clientCode}"?`)) {
            handleDelete(clientId, clientCode);
        }
    };

    // Cell rendering
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 500 }}>
                        {index + 1}
                    </Box>
                );

            case 'client_code':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'client_logo':
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {value ? (
                            <Avatar
                                src={value}
                                alt="Client Logo"
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: 'grey.200',
                                    '& img': {
                                        objectFit: 'contain'
                                    }
                                }}
                            />
                        ) : (
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                                <BusinessIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            </Avatar>
                        )}
                    </Box>
                );

            case 'status':
                return (
                    <Chip
                        label={value}
                        color={value === 'Active' ? 'success' : 'default'}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                );

            case 'page_privileges':
                return (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        maxWidth: '100%'
                    }}>
                        {value && value.length > 0 ? (
                            value.slice(0, 3).map((privilege, idx) => (
                                <Chip
                                    key={idx}
                                    label={privilege}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        fontSize: '0.65rem',
                                        height: 20,
                                        fontFamily: 'Poppins',
                                        '& .MuiChip-label': {
                                            px: 0.8,
                                            py: 0
                                        }
                                    }}
                                />
                            ))
                        ) : (
                            <Chip
                                label="All pages"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    fontFamily: 'Poppins'
                                }}
                            />
                        )}
                        {value && value.length > 3 && (
                            <Typography variant="caption" sx={{
                                color: 'text.secondary',
                                fontSize: '0.65rem',
                                alignSelf: 'center',
                                ml: 0.5,
                                fontFamily: 'Poppins'
                            }}>
                                +{value.length - 3} more
                            </Typography>
                        )}
                    </Box>
                );

            case 'created_at':
                return (
                    <Typography variant="body2" sx={{
                        fontSize: '12px',
                        color: '#666',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'actions':
                return (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: 18, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(row.client_id, row.client_code);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 18, color: '#666' }} />
                        </IconButton>
                    </Stack>
                );
            default:
                return value || 'N/A';
        }
    };

    return (
        <CommonTable
            columns={columns}
            data={tableData}
            renderCustomCell={renderCell}
            onRowClick={handleRowSelect}
            selectedRow={selectedRow}
            actionButtons={actionButtons}
            isLoading={isLoading}
            searchPlaceholder="Search clients..."
        />
    );
};

export default ClientTable;